/**
 * ParagraphSliceFinder - Utility for finding paragraph slices in page layouts
 * 
 * Extracted from TypesetterWorkerClient for better modularity and testability.
 * 
 * This utility provides functions for finding paragraph slices within page layouts,
 * which is useful for calculating spill frontiers and determining line positions.
 */

import type { PageLayout } from '../../../core/types/Typesetter';

export interface ParagraphSlice {
  paragraph_id: string;
  line_start: number;
  [key: string]: any;
}

/**
 * Find a paragraph slice in a page layout
 * 
 * Searches for a paragraph slice with the given paragraph_id in the page's paragraphs array.
 * 
 * @param page - PageLayout object to search
 * @param paragraphId - Paragraph ID to find
 * @returns Paragraph slice if found, or null if not found
 */
export function findParagraphSliceInPage(
  page: PageLayout,
  paragraphId: string
): ParagraphSlice | null {
  const slice = page.paragraphs.find(s => s.paragraph_id === paragraphId);
  return slice || null;
}

/**
 * Find the starting line index for a paragraph in a page
 * 
 * Searches for a paragraph slice and returns its line_start value.
 * 
 * @param page - PageLayout object to search
 * @param paragraphId - Paragraph ID to find
 * @returns Starting line index, or null if paragraph not found in page
 */
export function findParagraphLineStartInPage(
  page: PageLayout,
  paragraphId: string
): number | null {
  const slice = findParagraphSliceInPage(page, paragraphId);
  return slice ? slice.line_start : null;
}

/**
 * Find all paragraph slices for a given paragraph ID across multiple pages
 * 
 * Searches through an array of pages to find all slices of a specific paragraph.
 * 
 * @param pages - Array of PageLayout objects to search
 * @param paragraphId - Paragraph ID to find
 * @returns Array of paragraph slices found across all pages
 */
export function findAllParagraphSlices(
  pages: PageLayout[],
  paragraphId: string
): ParagraphSlice[] {
  const slices: ParagraphSlice[] = [];
  
  for (const page of pages) {
    const slice = findParagraphSliceInPage(page, paragraphId);
    if (slice) {
      slices.push(slice);
    }
  }
  
  return slices;
}

